import { useRef, useState } from "react";

import AnnotationAlert from "@uui/AnnotationAlert";
import AnnotationDots from "@uui/AnnotationDots";
import AnnotationQuestion from "@uui/AnnotationQuestion";
import AnnotationX from "@uui/AnnotationX";
import FaceIcon from "@uui/FaceSmile";
import ScaleIcon from "@uui/Scales01";
import SpeedometerIcon from "@uui/Speedometer03";
import {
  camelCase,
  difference,
  every,
  isEmpty,
  isEqual,
  kebabCase,
  uniq,
} from "lodash/fp";
import { useNavigate, useParams } from "react-router-dom";

import { Typography } from "@mui/material";

import ActionBar from "@app/analytics/components/ActionBar";
import { CoachingModuleTitle } from "@app/analytics/components/CoachingModule";
import DateSelector from "@app/analytics/components/DateSelector";
import useDefaultSection from "@app/analytics/hooks/useDefaultSection";
import { getRoundedValues } from "@app/analytics/utils";
import { TOOLTIPS } from "@app/common/content";
import ROUTES from "@app/common/routes";
import { SemanticScore } from "@app/common/utils";
import { usePageTitle } from "@app/utils/hooks";

import Stack, { Column, Row } from "@rui/Stack";

import Bias from "./Bias";
import Charisma from "./Charisma";
import FillerWords from "./FillerWords";
import useCoachingDashboardCharismaBias from "./hooks/useCoachingDashboardCharismaBias";
import useCoachingDashboardData from "./hooks/useCoachingDashboardData";
import Interruptions from "./Interruptions";
import NonInclusiveTerms from "./NonInclusiveTerms";
import QuestionsAsked from "./QuestionsAsked";
import {
  ContentContainer,
  Description,
  FeatureHeader,
  FeatureHeading,
  FeatureItem,
  FeatureScoreChip,
  LeftContainer,
  RightOuterContainer,
  RightInnerContainer,
  ValueWithSemanticIcon,
} from "./styles";
import TalkingPace from "./TalkingPace";
import UnlockCoaching from "./UnlockCoaching";
import UnlockCoachingInsights from "./UnlockCoachingInsights";

type CoachingSection =
  | "talkingPace"
  | "fillerWords"
  | "nonInclusiveTerms"
  | "interruptions"
  | "bias"
  | "charisma"
  | "questionsAsked";

const COACHING_SECTIONS = [
  "talking-pace",
  "filler-words",
  "non-inclusive-terms",
  "interruptions",
  "bias",
  "charisma",
  "questions-asked",
];

const getAggregatedScore = (
  scores: SemanticScore[],
  isLoading: boolean
): SemanticScore => {
  if (isLoading) return "unknown";

  if (isEmpty(difference(uniq(scores), ["positive", "unknown"]))) {
    return every((s) => isEqual(s, "unknown"), scores) ? "unknown" : "positive";
  }

  return "negative";
};

const Coaching = () => {
  usePageTitle("Coaching");

  const navigate = useNavigate();
  const { section } = useParams();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useDefaultSection({
    root: ROUTES.COACHING_ROOT,
    defaultRoute: ROUTES.COACHING_DEFAULT_SECTION,
    selected: section,
    sections: COACHING_SECTIONS,
  });

  const {
    fillerWords,
    interruptions,
    noData: noCoachingData,
    nonInclusiveTerms,
    questions,
    sessionCount,
    talkingPace,
    isInitialLoading: isCoachingDataLoading,
  } = useCoachingDashboardData();

  const {
    bias,
    charisma,
    noData: noCharismaBiasData,
    isInitialLoading: isCharismaBiasDataLoading,
  } = useCoachingDashboardCharismaBias();

  const defaultSection =
    section && COACHING_SECTIONS.includes(section)
      ? (camelCase(section) as CoachingSection)
      : "talkingPace";

  const [selectedSection, setSelectedSection] =
    useState<CoachingSection>(defaultSection);

  const handleSelectSection = (section: CoachingSection) => () => {
    setSelectedSection(section);
    navigate(`${ROUTES.COACHING_ROOT}/${kebabCase(section)}`);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const isLoading = isCoachingDataLoading || isCharismaBiasDataLoading;
  const noData = noCoachingData && noCharismaBiasData;

  const clarityScore = getAggregatedScore(
    [talkingPace.semanticScore, fillerWords.semanticScore],
    isCoachingDataLoading
  );
  const inclusionScore = getAggregatedScore(
    [nonInclusiveTerms.semanticScore, interruptions.semanticScore],
    isCoachingDataLoading
  );
  const impactScore = getAggregatedScore(
    [bias.semanticScore, charisma.semanticScore, questions.semanticScore],
    isLoading
  );

  return (
    <Stack>
      <ActionBar>
        <Typography variant="h2">Coaching</Typography>
        <DateSelector />
      </ActionBar>
      <ContentContainer>
        <LeftContainer spacing={3}>
          <UnlockCoachingInsights sessionCount={sessionCount} />
          <Column>
            <Row variant="apart">
              <FeatureHeader>
                <FeatureHeading>Clarity</FeatureHeading>
                {clarityScore !== "unknown" && (
                  <FeatureScoreChip variant={clarityScore} />
                )}
              </FeatureHeader>
              <Description>Your averages for the selected period</Description>
            </Row>
            <FeatureItem
              onClick={handleSelectSection("talkingPace")}
              selected={selectedSection === "talkingPace"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={SpeedometerIcon}
                  tooltip={TOOLTIPS.coaching.talkingPace}
                >
                  Talking pace
                </CoachingModuleTitle>
              </Row>
              {talkingPace.rate != null && (
                <Row variant="right">
                  <ValueWithSemanticIcon score={talkingPace.semanticScore}>
                    {talkingPace.rate} wpm
                  </ValueWithSemanticIcon>
                </Row>
              )}
            </FeatureItem>
            <FeatureItem
              onClick={handleSelectSection("fillerWords")}
              selected={selectedSection === "fillerWords"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={AnnotationDots}
                  tooltip={TOOLTIPS.coaching.fillerWords}
                >
                  Filler words
                </CoachingModuleTitle>
              </Row>
              {fillerWords.rate != null && (
                <Row variant="right">
                  <ValueWithSemanticIcon score={fillerWords.semanticScore}>
                    {Math.round(fillerWords.rate)}% of speech
                  </ValueWithSemanticIcon>
                </Row>
              )}
            </FeatureItem>
          </Column>
          <Column>
            <Row variant="apart">
              <FeatureHeader>
                <FeatureHeading>Inclusion</FeatureHeading>
                {inclusionScore !== "unknown" && (
                  <FeatureScoreChip variant={inclusionScore} />
                )}
              </FeatureHeader>
              <Description>Your averages for the selected period</Description>
            </Row>
            <FeatureItem
              onClick={handleSelectSection("nonInclusiveTerms")}
              selected={selectedSection === "nonInclusiveTerms"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={AnnotationX}
                  tooltip={TOOLTIPS.coaching.nonInclusiveTerms}
                >
                  Non-inclusive terms
                </CoachingModuleTitle>
              </Row>
              {nonInclusiveTerms.average.value != null && (
                <Row variant="right">
                  <ValueWithSemanticIcon
                    score={nonInclusiveTerms.semanticScore}
                  >
                    {nonInclusiveTerms.average.formattedValue} per meeting
                  </ValueWithSemanticIcon>
                </Row>
              )}
            </FeatureItem>
            <FeatureItem
              onClick={handleSelectSection("interruptions")}
              selected={selectedSection === "interruptions"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={AnnotationAlert}
                  tooltip={TOOLTIPS.coaching.interruptions}
                >
                  Interruptions
                </CoachingModuleTitle>
              </Row>
              {interruptions.rate != null && (
                <Row variant="right">
                  <ValueWithSemanticIcon score={interruptions.semanticScore}>
                    {getRoundedValues(interruptions.rate).formattedValue} per 10
                    turns
                  </ValueWithSemanticIcon>
                </Row>
              )}
            </FeatureItem>
          </Column>
          <Column>
            <Row variant="apart">
              <FeatureHeader>
                <FeatureHeading>Impact</FeatureHeading>
                {impactScore !== "unknown" && (
                  <FeatureScoreChip variant={impactScore} />
                )}
              </FeatureHeader>
              <Description>Your averages for the selected period</Description>
            </Row>
            <FeatureItem
              onClick={handleSelectSection("bias")}
              selected={selectedSection === "bias"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={ScaleIcon}
                  tooltip={TOOLTIPS.coaching.bias}
                >
                  Bias
                </CoachingModuleTitle>
              </Row>
              <Row variant="right">
                <ValueWithSemanticIcon score={bias.semanticScore}>
                  {bias.average}
                </ValueWithSemanticIcon>
              </Row>
            </FeatureItem>
            <FeatureItem
              onClick={handleSelectSection("charisma")}
              selected={selectedSection === "charisma"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={FaceIcon}
                  tooltip={TOOLTIPS.coaching.charisma}
                >
                  Charisma
                </CoachingModuleTitle>
              </Row>
              <Row variant="right">
                <ValueWithSemanticIcon score={charisma.semanticScore}>
                  {charisma.average}
                </ValueWithSemanticIcon>
              </Row>
            </FeatureItem>
            <FeatureItem
              onClick={handleSelectSection("questionsAsked")}
              selected={selectedSection === "questionsAsked"}
            >
              <Row variant="left">
                <CoachingModuleTitle
                  Icon={AnnotationQuestion}
                  tooltip={TOOLTIPS.coaching.questionsAsked}
                >
                  Questions asked
                </CoachingModuleTitle>
              </Row>
              {questions.average.value != null && (
                <Row variant="right">
                  <ValueWithSemanticIcon score={questions.semanticScore}>
                    {questions.average.formattedValue} per meeting
                  </ValueWithSemanticIcon>
                </Row>
              )}
            </FeatureItem>
          </Column>
        </LeftContainer>
        <RightOuterContainer ref={scrollContainerRef}>
          {noData ? (
            <UnlockCoaching />
          ) : (
            <RightInnerContainer spacing={3}>
              {selectedSection === "talkingPace" && <TalkingPace />}
              {selectedSection === "fillerWords" && <FillerWords />}
              {selectedSection === "nonInclusiveTerms" && <NonInclusiveTerms />}
              {selectedSection === "interruptions" && <Interruptions />}
              {selectedSection === "bias" && <Bias />}
              {selectedSection === "charisma" && <Charisma />}
              {selectedSection === "questionsAsked" && <QuestionsAsked />}
            </RightInnerContainer>
          )}
        </RightOuterContainer>
      </ContentContainer>
    </Stack>
  );
};

export default Coaching;
